import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";

import { AccessMode, ViewMode } from "@superblocksteam/shared";
import type { TokenVaultSeedRequest } from "@superblocksteam/shared";

import TokenProvider from "auth/token";
import {
  completeTokenVaultSeed,
  startTokenVaultSeed,
} from "core/auth/tokenVaultSeed";
import envs from "env";
import { getAppViewMode } from "legacy/selectors/applicationSelectors";
import { getAccessMode } from "legacy/selectors/usersSelectors";
import { GENERATE_TOKEN } from "pages/routes";
import { HttpMethod, callServer } from "store/utils/client";

export const isExpired = (jwt: string) => {
  try {
    const decoded = JSON.parse(atob(jwt.split(".")[1]));
    return decoded.exp < Date.now() / 1000;
  } catch {
    return false;
  }
};

function useJwt(): [string | undefined, () => void, boolean] {
  const [loading, setLoading] = useState(true);
  const storedToken = TokenProvider.getToken();
  const accessMode = useSelector(getAccessMode);
  const appViewMode = useSelector(getAppViewMode);
  const tokenViewMode = appViewMode ?? ViewMode.DEPLOYED;
  const viewModeRef = useRef<ViewMode | null>(tokenViewMode);

  const fetchJwt = useCallback(async () => {
    if (accessMode === AccessMode.EXTERNAL_USER) {
      // already have the JWT
      return;
    } else if (accessMode === AccessMode.VISITOR) {
      // don't need a JWT
      return;
    }
    try {
      const response = await callServer<{
        access_token: string;
        token_vault_seed?: TokenVaultSeedRequest;
      }>(
        {
          method: HttpMethod.Post,
          url: GENERATE_TOKEN,
          body: {
            view_mode: tokenViewMode,
          },
        },
        {
          notifyOnError: false,
          shouldCrashApp: false,
        },
      );
      if (response.access_token) {
        TokenProvider.setToken(response.access_token);
      }
      if (response.token_vault_seed) {
        // Awaited, not fired and forgotten: this navigates the tab to the identity
        // provider, and the caller is still holding the loading state here. Firing
        // it off would let the app paint and become interactive first, so the
        // navigation would land a second or two later and read as the page
        // redirecting itself for no reason. Every step inside is time-bounded.
        await startTokenVaultSeed({
          domain: envs.get("SUPERBLOCKS_UI_AUTH_ISSUER_URL"),
          seed: response.token_vault_seed,
        });
      }
    } catch (error) {
      console.error("Could not fetch JWT", error);
    } finally {
      setLoading(false);
    }
  }, [accessMode, tokenViewMode]);

  // Runs on every load, because the page we come back to after the Token Vault
  // handshake may already hold a valid token and skip the mint below.
  useEffect(() => {
    void completeTokenVaultSeed({
      domain: envs.get("SUPERBLOCKS_UI_AUTH_ISSUER_URL"),
    });
  }, []);

  useEffect(() => {
    if (
      accessMode === AccessMode.EXTERNAL_USER ||
      accessMode === AccessMode.VISITOR
    ) {
      // in this case the jwt is passed to the embed or the user is a visitor, we should never fetch it
      setLoading(false);
      return;
    } else if (
      storedToken &&
      !isExpired(storedToken) &&
      viewModeRef.current === tokenViewMode
    ) {
      setLoading(false);
    } else {
      setLoading(true);
      viewModeRef.current = tokenViewMode;
      fetchJwt();
    }
  }, [fetchJwt, storedToken, accessMode, tokenViewMode]);

  return [storedToken, fetchJwt, loading];
}

export default useJwt;
